/**
 * Performance Tab Constants
 *
 * Section configurations and rating colors for the Performance Scorecard.
 */

// Section configuration for the scorecard rows
export interface SectionConfig {
  key: string
  label: string
  bgColor: string
  textColor: string
  /** Left accent stripe on the metric card (replaces the full-width coloured band). */
  accentBar: string
  /** Accent text colour for the card's section label. */
  accentText: string
  /** One-line description shown under the section label. */
  description: string
}

export const SECTIONS: SectionConfig[] = [
  { key: 'line_orders', label: 'Line Orders', bgColor: 'bg-blue-500', textColor: 'text-white', accentBar: 'bg-blue-500', accentText: 'text-blue-700', description: 'Lines delivered against lines expected' },
  { key: 'delivery_performance', label: 'Delivery Performance', bgColor: 'bg-green-500', textColor: 'text-white', accentBar: 'bg-green-500', accentText: 'text-green-700', description: 'On time, in full' },
  { key: 'cost', label: 'Cost', bgColor: 'bg-orange-500', textColor: 'text-white', accentBar: 'bg-orange-500', accentText: 'text-orange-700', description: 'Value of goods received' },
  { key: 'returns', label: 'Returns', bgColor: 'bg-red-500', textColor: 'text-white', accentBar: 'bg-red-500', accentText: 'text-red-700', description: 'Returns raised to the supplier' },
  { key: 'quality', label: 'Quality', bgColor: 'bg-gray-500', textColor: 'text-white', accentBar: 'bg-slate-500', accentText: 'text-slate-700', description: 'Concessions' },
  { key: 'supplier_concerns', label: 'Supplier Concerns', bgColor: 'bg-teal-500', textColor: 'text-white', accentBar: 'bg-teal-500', accentText: 'text-teal-700', description: 'Non-conformance reports raised' },
]

// Rating color classes
export const RATING_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  green: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-500' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-500' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-500' },
  red: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-500' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-500' },
}

/**
 * Get rating color classes for a given color key
 */
export function getRatingColors(colorKey: string) {
  return RATING_COLORS[colorKey] || RATING_COLORS['gray']
}

/**
 * Format date as YYYY-MM-DD in local timezone (avoids UTC conversion issues)
 */
function formatLocalDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Get default date range (last completed month)
 * CR-6310: Atlas Copco uses this as a monthly review tool, so default
 * to the last completed month, not the current month.
 * Start Date: 1st of previous month
 * End Date: Last day of previous month
 */
export function getDefaultDateRange() {
  const now = new Date()
  // Last completed month: go back one month from current
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const startOfLastMonth = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1)
  const endOfLastMonth = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0)
  return {
    startDate: formatLocalDate(startOfLastMonth),
    endDate: formatLocalDate(endOfLastMonth),
  }
}

export interface SupplierOption {
  id: number
  name: string
}

export interface SectionDataItem {
  ytd: string
  month: string
  ytdLabel: string
  monthLabel: string
}

export type SectionDataMap = Record<string, SectionDataItem>

/**
 * CR-6310: Drilldown configuration for each scorecard section.
 * Maps section keys to drilldown metrics or navigation URLs.
 */
export interface DrilldownConfig {
  /** Drilldown metric key (for DrilldownTrigger) */
  metric?: string
  /** Navigation URL pattern (for direct navigation) - use {supplier}, {startDate}, {endDate} placeholders */
  navUrl?: string
  /** Additional filters for drilldown - values should be strings (e.g., 'true' not true) */
  extraFilters?: Record<string, string>
  /** Context label suffix */
  labelSuffix?: string
}

export const SECTION_DRILLDOWN_CONFIG: Record<string, { ytd: DrilldownConfig; month: DrilldownConfig }> = {
  line_orders: {
    ytd: { metric: 'otif', labelSuffix: 'Line Orders YTD' },
    month: { metric: 'otif', labelSuffix: 'Line Orders Month' },
  },
  delivery_performance: {
    ytd: { metric: 'otif', labelSuffix: 'Delivery Performance YTD' },
    month: { metric: 'otif', labelSuffix: 'Delivery Performance Month' },
  },
  cost: {
    ytd: { metric: 'spend', labelSuffix: 'Cost YTD' },
    month: { metric: 'spend', labelSuffix: 'Cost Month' },
  },
  returns: {
    ytd: {
      metric: 'ncr',
      extraFilters: { is_collection_required: 'true' },
      labelSuffix: 'Returns YTD',
    },
    month: {
      metric: 'ncr',
      extraFilters: { is_collection_required: 'true' },
      labelSuffix: 'Returns Month',
    },
  },
  quality: {
    ytd: { navUrl: '/quality?tab=concessions&supplier={supplier}&date_from={startDate}&date_to={endDate}' },
    month: { navUrl: '/quality?tab=concessions&supplier={supplier}&date_from={startDate}&date_to={endDate}' },
  },
  supplier_concerns: {
    ytd: { metric: 'ncr', labelSuffix: 'Supplier Concerns YTD' },
    month: { metric: 'ncr', labelSuffix: 'Supplier Concerns Month' },
  },
}
